import React, { useEffect, useState } from "react";
import {
  List,
  Button,
  Card,
  Tag,
  Row,
  Col,
  message,
  Input,
  Modal,
  Form,
  Select,
  Alert,
  Typography,
  Skeleton,
  Popconfirm,
  Space,
  Tooltip,
} from "antd";
import {
  SearchOutlined,
  EditOutlined,
  FolderFilled,
  FolderOpenOutlined,
  PlusOutlined,
  InboxOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EyeOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import MarketPlaceService from "../../../utils/api/marketplace";
import NewProduct from "../global_components/NewProduct";
import { useMediaQuery } from "react-responsive";
import CategorySelectorModal from "./CategorySelector";
import "../supplier/productLit.css";

const { Search } = Input;
const { Option } = Select;

const ProductList = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  let history = useNavigate();

  const [open, setOpen] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vendor, setVendor] = useState({});
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [selectedCategoryChain, setSelectedCategoryChain] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [editImagePreviews, setEditImagePreviews] = useState([]);
  const [editImageFiles, setEditImageFiles] = useState([]);
  const [editPriceTooltipOpen, setEditPriceTooltipOpen] = useState(false);

  const isPendingApproval = (product) => product?.archive === true;
  const isArchivedProduct = (product) =>
    Boolean(product?.archive) && !isPendingApproval(product);
  const isEditingArchived = isArchivedProduct(editingProduct);

  const isSmallScreen = useMediaQuery({ query: "(max-width: 768px)" });
  const [form] = Form.useForm();

  useEffect(() => {
    loadProducts();
    loadAllCategories();
  }, []);

  useEffect(() => {
    if (searchValue.trim() === "") {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(
        (product) =>
          product.name.toLowerCase().includes(searchValue.toLowerCase()) ||
          (product.description &&
            product.description
              .toLowerCase()
              .includes(searchValue.toLowerCase())) ||
          (product.sku &&
            product.sku.toLowerCase().includes(searchValue.toLowerCase()))
      );
      setFilteredProducts(filtered);
    }
  }, [searchValue, products]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const supplierId = user.id;

      if (!supplierId) {
        message.error("Error: No se pudo identificar el usuario");
        setLoading(false);
        return;
      }

      const productsResp = await MarketPlaceService.getProductsBySupplier(
        supplierId
      );
      const productsData = productsResp.data.products || [];

      setProducts(productsData);
      setFilteredProducts(productsData);

      const vendorResp = await MarketPlaceService.getVendorByUser(user.id);
      setVendor(vendorResp.data);
    } catch (error) {
      message.error("Error al cargar los productos");
      setProducts([]);
      setFilteredProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAllCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await MarketPlaceService.getCategories();

      let categoriesData = [];

      if (
        response.data &&
        response.data.success &&
        Array.isArray(response.data.categories)
      ) {
        categoriesData = response.data.categories;
      } else if (response.data && Array.isArray(response.data)) {
        categoriesData = response.data;
      } else if (Array.isArray(response)) {
        categoriesData = response;
      }

      const validCategories = categoriesData
        .filter((cat) => cat && cat.name && cat.slug)
        .map((cat) => ({
          id: cat.id,
          name: cat.name,
          slug: cat.slug,
          parent_slug: cat.parent_slug || null,
          path: cat.path || cat.slug,
        }));

      setAllCategories(validCategories);
      const mainCategories = validCategories.filter((cat) => !cat.parent_slug);
      setCategories(mainCategories);
    } catch (error) {
      message.error("Error al cargar las categorías");
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadSubcategories = async (categorySlug) => {
    try {
      const response = await MarketPlaceService.getSubcategoriesByCategory(
        categorySlug
      );

      let subcategoriesData = [];

      if (
        response.data &&
        response.data.success &&
        Array.isArray(response.data.categories)
      ) {
        subcategoriesData = response.data.categories;
      } else if (response.data && Array.isArray(response.data)) {
        subcategoriesData = response.data;
      } else if (Array.isArray(response)) {
        subcategoriesData = response;
      }

      const filteredSubcategories = subcategoriesData.filter(
        (subcat) =>
          subcat.parent_slug === categorySlug ||
          subcat.parent === categorySlug ||
          (subcat.parent && subcat.parent.slug === categorySlug)
      );

      setSubcategories(filteredSubcategories);

      if (
        filteredSubcategories.length === 1 &&
        !form.getFieldValue("subcategory")
      ) {
        form.setFieldsValue({
          subcategory: filteredSubcategories[0].slug,
        });
      }
    } catch (error) {
      console.error("Error al cargar subcategorías:", error);
      setSubcategories([]);
    }
  };

  const handleCategoryChange = (value) => {
    if (value) {
      loadSubcategories(value);
      form.setFieldsValue({
        subcategory: undefined,
      });
    } else {
      setSubcategories([]);
      form.setFieldsValue({
        subcategory: undefined,
      });
    }
  };

  const handleCategorySelect = (selection) => {
    setSelectedCategoryChain(selection.categoryChain);

    form.setFieldsValue({
      category: selection.categoryChain[0],
      subcategory: selection.categoryChain[selection.categoryChain.length - 1],
    });

    setCategoryModalOpen(false);
  };

  const handleCardClick = (product) => {
    if (!product.archive) {
      history(
        `/marketplace/${product.category}/${product.sub_category}/${
          product.id
        }?name=${encodeURIComponent(product.slug)}`
      );
    }
  };

  const handleNewProduct = () => {
    setOpen(true);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
  };

  const handleSearch = (value) => {
    setSearchValue(value);
  };

  const handleEditImageUpload = (event, index = null) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      if (index !== null) {
        const newPreviews = [...editImagePreviews];
        const newFiles = [...editImageFiles];
        newPreviews[index] = e.target.result;
        newFiles[index] = file;
        setEditImagePreviews(newPreviews);
        setEditImageFiles(newFiles);
        message.success("Imagen actualizada");
      } else {
        if (editImageFiles.length >= 2) {
          message.error("Solo puedes subir máximo 2 imágenes");
          return;
        }
        setEditImagePreviews((prev) => [...prev, e.target.result]);
        setEditImageFiles((prev) => [...prev, file]);
      }
    };
    reader.readAsDataURL(file);

    event.target.value = "";
  };

  const handleEditRemoveImage = (index) => {
    setEditImagePreviews((prev) => prev.filter((_, i) => i !== index));
    setEditImageFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEdit = (product) => {
    if (isPendingApproval(product)) {
      message.info(
        "Este producto está pendiente de aprobación y no puede ser editado"
      );
      return;
    }

    setEditingProduct(product);

    const previews = [];
    const files = [];

    if (product.images && product.images.length > 0) {
      product.images.forEach((imgUrl, index) => {
        previews.push(imgUrl);
        files.push({ url: imgUrl, isExisting: true });
      });
    }

    setEditImagePreviews(previews);
    setEditImageFiles(files);
    setEditModalVisible(true);

    form.resetFields();

    if (product.category) {
      const categorySlug =
        typeof product.category === "string"
          ? product.category
          : product.category.slug || product.category;

      loadSubcategories(categorySlug).then(() => {
        setTimeout(() => {
          let currentSubcategoryValue = product.sub_category;

          if (Array.isArray(product.sub_category)) {
            currentSubcategoryValue = product.sub_category[0];
          }

          let subcategorySlug = currentSubcategoryValue;

          if (currentSubcategoryValue && allCategories.length > 0) {
            const matchingCategory = allCategories.find(
              (cat) =>
                cat.name === currentSubcategoryValue ||
                cat.slug === currentSubcategoryValue ||
                (cat.slug && cat.slug.includes(currentSubcategoryValue))
            );

            if (matchingCategory) {
              subcategorySlug = matchingCategory.slug;
            }
          }

          form.setFieldsValue({
            name: product.name,
            description: product.description,
            price: product.price,
            stock: product.stock,
            sku: product.sku || "",
            weight_kg:
              product.weight_kg !== undefined && product.weight_kg !== null
                ? String(product.weight_kg)
                : "",
            height_cm:
              product.height_cm !== undefined && product.height_cm !== null
                ? String(product.height_cm)
                : "",
            width_cm:
              product.width_cm !== undefined && product.width_cm !== null
                ? String(product.width_cm)
                : "",
            length_cm:
              product.length_cm !== undefined && product.length_cm !== null
                ? String(product.length_cm)
                : "",
            category: product.category,
            subcategory: subcategorySlug || currentSubcategoryValue,
          });
        }, 100);
      });
    } else {
      setTimeout(() => {
        form.setFieldsValue({
          name: product.name,
          description: product.description,
          price: product.price,
          stock: product.stock,
          sku: product.sku || "",
          weight_kg:
            product.weight_kg !== undefined && product.weight_kg !== null
              ? String(product.weight_kg)
              : "",
          height_cm:
            product.height_cm !== undefined && product.height_cm !== null
              ? String(product.height_cm)
              : "",
          width_cm:
            product.width_cm !== undefined && product.width_cm !== null
              ? String(product.width_cm)
              : "",
          length_cm:
            product.length_cm !== undefined && product.length_cm !== null
              ? String(product.length_cm)
              : "",
          category: product.category,
          subcategory: product.sub_category,
        });
      }, 100);
    }
  };

  const onSubmit = async (values) => {
    try {
      const imageBase64Array = [];
      const weightKg = Number(values.weight_kg);
      const heightCm = Number(values.height_cm);
      const widthCm = Number(values.width_cm);
      const lengthCm = Number(values.length_cm);

      if (values.images && values.images.length > 0) {
        for (const imageFile of values.images) {
          if (imageFile.originFileObj) {
            const base64 = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.readAsDataURL(imageFile.originFileObj);
            });
            imageBase64Array.push(base64);
          } else if (imageFile.thumbUrl) {
            imageBase64Array.push(imageFile.thumbUrl);
          }
        }
      }

      const newProduct = {
        supplier_id: user.id,
        sku: values.sku || "N/A",
        name: values.name,
        description: values.description,
        stock: parseInt(values.stock),
        price: parseFloat(values.price),
        newPrice: Number((parseFloat(values.price) * 1.28).toFixed(2)),
        category: values.category,
        sub_category: values.subcategory,
        image1Base64: imageBase64Array[0] || null,
        image2Base64: imageBase64Array[1] || null,
        weight_kg: Number.isFinite(weightKg) ? weightKg : 0,
        height_cm: Number.isFinite(heightCm) ? heightCm : 0,
        width_cm: Number.isFinite(widthCm) ? widthCm : 0,
        length_cm: Number.isFinite(lengthCm) ? lengthCm : 0,
        archive: true,
      };

      if (process.env.NODE_ENV !== "production") {
        console.log("[product/new] payload:", newProduct);
      }

      message.loading({ content: "Creando producto...", key: "creating" });

      const createResponse = await MarketPlaceService.newProduct(newProduct);

      if (process.env.NODE_ENV !== "production") {
        console.log("[product/new] response:", createResponse?.data || createResponse);
      }

      const createdProductId =
        createResponse?.data?.product_id ||
        createResponse?.data?.productId ||
        createResponse?.data?.data?.product_id ||
        createResponse?.data?.data?.productId ||
        createResponse?.data?.id ||
        createResponse?.data?.data?.id ||
        null;

      if (createdProductId) {
        try {
          const syncPayload = {
            name: values.name,
            description: values.description,
            sku: values.sku || "N/A",
            stock: parseInt(values.stock),
            price: parseFloat(values.price),
            newPrice: Number((parseFloat(values.price) * 1.28).toFixed(2)),
            category: values.category,
            sub_category: [values.subcategory],
            archive: true,
            weight_kg: Number.isFinite(weightKg) ? weightKg : 0,
            height_cm: Number.isFinite(heightCm) ? heightCm : 0,
            width_cm: Number.isFinite(widthCm) ? widthCm : 0,
            length_cm: Number.isFinite(lengthCm) ? lengthCm : 0,
            weightKg: Number.isFinite(weightKg) ? weightKg : 0,
            heightCm: Number.isFinite(heightCm) ? heightCm : 0,
            widthCm: Number.isFinite(widthCm) ? widthCm : 0,
            lengthCm: Number.isFinite(lengthCm) ? lengthCm : 0,
          };

          if (process.env.NODE_ENV !== "production") {
            console.log("[product/update] sync payload:", syncPayload);
          }

          await MarketPlaceService.updateProduct(syncPayload, createdProductId);
        } catch (syncError) {
          message.warning(
            "El producto se creó, pero no se pudieron sincronizar las medidas automáticamente"
          );

          if (process.env.NODE_ENV !== "production") {
            console.error("[product/update] sync error:", syncError);
          }
        }
      } else if (process.env.NODE_ENV !== "production") {
        console.warn("[product/new] No se pudo leer product_id de la respuesta");
      }

      message.success({
        content:
          "Producto creado correctamente. Quedará pendiente de aprobación por el administrador.",
        key: "creating",
        duration: 3,
      });

      setOpen(false);
      await loadProducts();
    } catch (error) {
      message.error({
        content: "Hubo un error al crear el producto",
        key: "creating",
        duration: 3,
      });
    }
  };

  const handleUpdate = async (id, values) => {
    try {
      const shouldReactivateArchived = isArchivedProduct(editingProduct);
      const imageBase64Array = [];
      const weightKg = Number(
        values.weight_kg ?? values.weightKg ?? values.weight
      );
      const heightCm = Number(
        values.height_cm ?? values.heightCm ?? values.height
      );
      const widthCm = Number(values.width_cm ?? values.widthCm ?? values.width);
      const lengthCm = Number(
        values.length_cm ?? values.lengthCm ?? values.length
      );

      for (let i = 0; i < editImageFiles.length; i++) {
        const imgFile = editImageFiles[i];
        if (imgFile && !imgFile.isExisting) {
          const base64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(imgFile);
          });
          imageBase64Array.push(base64);
        }
      }

      const updateProduct = {
        name: values.name,
        description: values.description,
        price: parseFloat(values.price),
        newPrice: parseFloat(values.price) * 1.28,
        sku: values.sku || null,
        stock: parseInt(values.stock),
        weight_kg: Number.isFinite(weightKg) ? weightKg : 0,
        height_cm: Number.isFinite(heightCm) ? heightCm : 0,
        width_cm: Number.isFinite(widthCm) ? widthCm : 0,
        length_cm: Number.isFinite(lengthCm) ? lengthCm : 0,
        weight: Number.isFinite(weightKg) ? weightKg : 0,
        height: Number.isFinite(heightCm) ? heightCm : 0,
        width: Number.isFinite(widthCm) ? widthCm : 0,
        length: Number.isFinite(lengthCm) ? lengthCm : 0,
        category: values.category,
        sub_category: [values.subcategory],
        archive: false,
      };

      if (imageBase64Array.length > 0) {
        updateProduct.image1Base64 = imageBase64Array[0] || null;
        updateProduct.image2Base64 = imageBase64Array[1] || null;
      }

      message.loading({ content: "Actualizando producto...", key: "updating" });

      await MarketPlaceService.updateProduct(updateProduct, id);
      message.success({
        content: shouldReactivateArchived
          ? "Producto actualizado y reactivado para la venta"
          : "Producto actualizado con éxito",
        key: "updating",
        duration: 2,
      });

      await loadProducts();

      setEditModalVisible(false);
      form.resetFields();
      setSubcategories([]);
      setEditImagePreviews([]);
      setEditImageFiles([]);
    } catch (error) {
      message.error({
        content: "Error al actualizar el producto",
        key: "updating",
        duration: 3,
      });
    }
  };

  const handleCancelEdit = () => {
    setEditModalVisible(false);
    form.resetFields();
    setSubcategories([]);
    setEditImagePreviews([]);
    setEditImageFiles([]);
  };

  const toggleArchive = async (product) => {
    if (isPendingApproval(product)) {
      message.info(
        "Este producto está pendiente de aprobación, no se puede archivar/desarchivar"
      );
      return;
    }

    const updated = { archive: isArchivedProduct(product) ? false : "archived" };
    try {
      await MarketPlaceService.updateProduct(updated, product.id);
      message.success(
        `Producto ${updated.archive ? "archivado" : "desarchivado"} con éxito`
      );

      setProducts((prev) =>
        prev.map((p) => (p.id === product.id ? { ...p, ...updated } : p))
      );
      setFilteredProducts((prev) =>
        prev.map((p) => (p.id === product.id ? { ...p, ...updated } : p))
      );
    } catch (err) {
      message.error("Error al actualizar el estado de archivo");
    }
  };

  const handleDelete = async (product) => {
    if (isPendingApproval(product)) {
      message.info(
        "Este producto está pendiente de aprobación, no se puede eliminar"
      );
      return;
    }

    try {
      if (!product || !product.id) {
        message.error({
          content: "El producto no tiene un ID válido",
          key: "deleting",
          duration: 3,
        });
        return;
      }

      message.loading({ content: "Eliminando producto...", key: "deleting" });

      await MarketPlaceService.deleteProduct(product.id);

      setProducts((prevProducts) =>
        prevProducts.filter((p) => p.id !== product.id)
      );
      setFilteredProducts((prevProducts) =>
        prevProducts.filter((p) => p.id !== product.id)
      );

      message.success({
        content: "Producto eliminado correctamente",
        key: "deleting",
        duration: 2,
      });
    } catch (error) {
      if (error.response && error.response.status === 404) {
        message.warning({
          content: "El producto ya no existe. Actualizando lista...",
          key: "deleting",
          duration: 2,
        });

        setProducts((prevProducts) =>
          prevProducts.filter((p) => p.id !== product.id)
        );
        setFilteredProducts((prevProducts) =>
          prevProducts.filter((p) => p.id !== product.id)
        );
      } else {
        message.error({
          content: "Error al eliminar. Intenta de nuevo.",
          key: "deleting",
          duration: 3,
        });
      }
    }
  };

  const renderProductCard = (product) => (
    <Card
      className={`product-card ${
        isPendingApproval(product)
          ? "pending"
          : isArchivedProduct(product)
          ? "archived"
          : ""
      }`}
      onClick={() => handleCardClick(product)}
      style={{ cursor: isPendingApproval(product) ? "default" : "pointer" }}
    >
      <div className="product-card-content">
        <div className="product-image-container">
          {product.images && product.images[0] ? (
            <img
              src={product.images[0]}
              alt={product.name}
              className="product-image"
            />
          ) : (
            <div className="product-no-image">
              <InboxOutlined style={{ fontSize: "36px" }} />
              <div>Sin imagen</div>
            </div>
          )}
        </div>

        <div className="product-info">
          <div className="product-status">
            {isPendingApproval(product) ? (
              <Tag color="orange" className="status-tag pending">
                Pendiente de aprobación
              </Tag>
            ) : isArchivedProduct(product) ? (
              <Tag className="status-tag archived">Archivado</Tag>
            ) : (
              <Tag className="status-tag active">Activo</Tag>
            )}
          </div>

          <h3 className="product-name">{product.name}</h3>

          <div className="product-meta">
            <h2 className="product-price">
              ${parseFloat(product.price).toFixed(2)}
            </h2>
            <div className="product-stock-container">
              <p
                className={`product-stock ${
                  product.stock > 0 ? "in-stock" : "out-of-stock"
                }`}
              >
                {product.stock > 0 ? (
                  <>
                    <CheckCircleOutlined className="stock-icon" />
                    {product.stock}
                  </>
                ) : (
                  <>
                    <CloseCircleOutlined className="stock-icon" />
                    Sin stock
                  </>
                )}
              </p>
            </div>
          </div>

          <div className="product-actions">
            <Tooltip
              title={
                isPendingApproval(product)
                  ? "Producto pendiente de aprobación, no se puede editar"
                  : "Editar producto"
              }
              placement="top"
            >
              <Button
                className="action-btn edit"
                icon={<EditOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(product);
                }}
                disabled={isPendingApproval(product)}
              />
            </Tooltip>

            <Tooltip
              title={
                isPendingApproval(product)
                  ? "Producto pendiente de aprobación"
                  : isArchivedProduct(product)
                  ? "Desarchivar producto"
                  : "Archivar producto"
              }
              placement="top"
            >
              <Button
                className={`action-btn ${
                  isArchivedProduct(product) ? "unarchive" : "archive"
                }`}
                icon={
                  isArchivedProduct(product) ? <FolderOpenOutlined /> : <FolderFilled />
                }
                onClick={(e) => {
                  e.stopPropagation();
                  toggleArchive(product);
                }}
                disabled={isPendingApproval(product)}
              />
            </Tooltip>

            <Popconfirm
              title="¿Eliminar producto?"
              description="¿Estás seguro de que quieres eliminar este producto? Esta acción no se puede deshacer."
              onConfirm={(e) => {
                e?.stopPropagation();
                handleDelete(product);
              }}
              onCancel={(e) => e?.stopPropagation()}
              okText="Sí, eliminar"
              cancelText="Cancelar"
              okType="danger"
            >
              <Tooltip
                title={
                  isPendingApproval(product)
                    ? "Producto pendiente de aprobación, no se puede eliminar"
                    : "Eliminar producto"
                }
                placement="top"
              >
                <Button
                  className="action-btn delete"
                  icon={<DeleteOutlined />}
                  onClick={(e) => e.stopPropagation()}
                  danger
                  disabled={isPendingApproval(product)}
                />
              </Tooltip>
            </Popconfirm>
          </div>
        </div>
      </div>
    </Card>
  );

  if (loading) {
    return (
      <div className="loading-skeleton">
        <div className="skeleton-grid">
          {[1, 2, 3, 4, 5, 6].map((item) => (
            <div key={item} className="skeleton-card">
              <Skeleton active paragraph={{ rows: 4 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="product-list-container">
      <div className="product-list-header">
        <div className="product-list-controls">
          <Input
            className="product-search"
            placeholder="Buscar productos..."
            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            onChange={handleSearchChange}
            onPressEnter={(e) => handleSearch(e.target.value)}
            allowClear
            size="large"
            value={searchValue}
            style={{ width: 500 }}
          />

          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleNewProduct}
            size="large"
            className="add-product-btn"
          >
            Agregar Producto
          </Button>
        </div>
      </div>

      {searchValue && (
        <div style={{ marginBottom: "16px", textAlign: "center" }}>
          <Typography.Text type="secondary">
            {filteredProducts.length === 0
              ? `No se encontraron productos para "${searchValue}"`
              : `Se encontraron ${filteredProducts.length} producto(s) para "${searchValue}"`}
          </Typography.Text>
        </div>
      )}

      {filteredProducts.length === 0 && !searchValue ? (
        <div className="empty-state">
          <InboxOutlined className="empty-state-icon" />
          <Typography.Title level={4} className="empty-state-title">
            {products.length === 0
              ? "No hay productos"
              : "No se encontraron productos"}
          </Typography.Title>
          <Typography.Text className="empty-state-description">
            {products.length === 0
              ? "Comienza agregando tu primer producto al catálogo."
              : "No se encontraron productos que coincidan con tu búsqueda."}
          </Typography.Text>
          <Button
            type="primary"
            onClick={handleNewProduct}
            size="large"
            className="add-product-btn"
          >
            {products.length === 0
              ? "Crear Primer Producto"
              : "Agregar Nuevo Producto"}
          </Button>
        </div>
      ) : (
        <div className="product-grid">
          <List
            dataSource={filteredProducts}
            renderItem={renderProductCard}
            pagination={{
              pageSize: 12,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} de ${total} productos`,
              className: "product-pagination",
            }}
          />
        </div>
      )}

      <Modal
        title="Editar Producto"
        open={editModalVisible}
        onCancel={handleCancelEdit}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          onFinish={(values) => handleUpdate(editingProduct?.id, values)}
          layout="vertical"
        >
          {isEditingArchived && (
            <Alert
              message="Producto archivado"
              description="Solo puedes modificar precio y stock. Al actualizar, el producto se activará nuevamente para la venta en marketplace."
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Form.Item
            name="name"
            label="Nombre del Producto"
            rules={[
              {
                required: true,
                message: "Por favor ingresa el nombre del producto",
              },
            ]}
          >
            <Input disabled={isEditingArchived} />
          </Form.Item>

          <Form.Item
            name="description"
            label="Descripción"
            rules={[
              { required: true, message: "Por favor ingresa la descripción" },
            ]}
          >
            <Input.TextArea rows={4} disabled={isEditingArchived} />
          </Form.Item>

          <Alert
            message="Selección de Categoría"
            description="Puedes seleccionar la categoría manualmente o usar el selector visual"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item>
                <Button
                  type="primary"
                  onClick={() => setCategoryModalOpen(true)}
                  style={{ marginBottom: 16 }}
                  loading={loadingCategories}
                  disabled={isEditingArchived}
                >
                  Usar Selector Visual de Categorías
                </Button>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="category"
                label="Categoría Principal"
                rules={[
                  {
                    required: true,
                    message: "Por favor selecciona la categoría",
                  },
                ]}
              >
                <Select
                  placeholder="Selecciona categoría"
                  loading={loadingCategories}
                  onChange={handleCategoryChange}
                  allowClear
                  disabled={isEditingArchived}
                >
                  {categories.map((category) => (
                    <Option key={category.slug} value={category.slug}>
                      {category.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="subcategory"
                label="Subcategoría"
                rules={[
                  {
                    required: true,
                    message: "Por favor selecciona la subcategoría",
                  },
                ]}
              >
                <Select
                  placeholder="Selecciona subcategoría"
                  disabled={isEditingArchived || !form.getFieldValue("category")}
                >
                  {subcategories.map((subcategory) => (
                    <Option key={subcategory.slug} value={subcategory.slug}>
                      {subcategory.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="price"
                label="Precio"
                rules={[
                  { required: true, message: "Por favor ingresa el precio" },
                ]}
              >
                <Input type="number" step="0.01" prefix="$" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="sku" label="SKU">
                <Input disabled={isEditingArchived} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="stock"
            label="Stock"
            rules={[{ required: true, message: "Por favor ingresa el stock" }]}
          >
            <Input type="number" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="weight_kg"
                label="Peso (kg)"
                rules={[
                  { required: true, message: "Por favor ingresa el peso" },
                ]}
              >
                <Input type="number" step="0.01" min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="height_cm"
                label="Alto (cm)"
                rules={[
                  { required: true, message: "Por favor ingresa el alto" },
                ]}
              >
                <Input type="number" step="0.01" min={0} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="width_cm"
                label="Ancho (cm)"
                rules={[
                  { required: true, message: "Por favor ingresa el ancho" },
                ]}
              >
                <Input type="number" step="0.01" min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="length_cm"
                label="Largo (cm)"
                rules={[
                  { required: true, message: "Por favor ingresa el largo" },
                ]}
              >
                <Input type="number" step="0.01" min={0} />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ marginBottom: 24 }}>
            <div style={{ marginBottom: 8 }}>
              <span
                style={{
                  color: "rgba(0, 0, 0, 0.88)",
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                Imágenes del Producto{" "}
                <span style={{ color: "#ff4d4f" }}>*</span>
              </span>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
              {editImagePreviews.map((preview, index) => (
                <div
                  key={index}
                  style={{
                    width: "100px",
                    height: "100px",
                    borderRadius: "8px",
                    overflow: "hidden",
                    position: "relative",
                    border: "1px solid #d9d9d9",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  <img
                    src={preview}
                    alt={`preview-${index}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />

                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: "rgba(0,0,0,0.5)",
                      opacity: 0,
                      transition: "opacity 0.3s",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
                    onMouseLeave={(e) => (e.currentTarget.style.opacity = "0")}
                  >
                    <Tooltip title="Actualizar foto">
                      <label style={{ cursor: "pointer" }}>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleEditImageUpload(e, index)}
                          style={{ display: "none" }}
                          disabled={isEditingArchived}
                        />
                        <div
                          style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "16px",
                            background: "white",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                          }}
                        >
                          <EditOutlined
                            style={{ color: "#1890ff", fontSize: "16px" }}
                          />
                        </div>
                      </label>
                    </Tooltip>

                    <Tooltip title="Eliminar foto">
                      <div
                        onClick={() => {
                          if (!isEditingArchived) {
                            handleEditRemoveImage(index);
                          }
                        }}
                        style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "16px",
                          background: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: isEditingArchived ? "not-allowed" : "pointer",
                          opacity: isEditingArchived ? 0.5 : 1,
                        }}
                      >
                        <DeleteOutlined
                          style={{ color: "#ff4d4f", fontSize: "16px" }}
                        />
                      </div>
                    </Tooltip>
                  </div>
                </div>
              ))}

              {editImagePreviews.length < 2 && (
                <div
                  style={{
                    width: "100px",
                    height: "100px",
                    borderRadius: "8px",
                    border: "2px dashed #d9d9d9",
                    backgroundColor: "#fafafa",
                    cursor: isEditingArchived ? "not-allowed" : "pointer",
                    position: "relative",
                    transition: "all 0.3s",
                    opacity: isEditingArchived ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (isEditingArchived) return;
                    e.currentTarget.style.borderColor = "#1890ff";
                    e.currentTarget.style.backgroundColor = "#e6f7ff";
                  }}
                  onMouseLeave={(e) => {
                    if (isEditingArchived) return;
                    e.currentTarget.style.borderColor = "#d9d9d9";
                    e.currentTarget.style.backgroundColor = "#fafafa";
                  }}
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleEditImageUpload(e)}
                    disabled={isEditingArchived}
                    style={{
                      position: "absolute",
                      width: "100%",
                      height: "100%",
                      opacity: isEditingArchived ? 0.35 : 0,
                      cursor: isEditingArchived ? "not-allowed" : "pointer",
                      zIndex: 2,
                    }}
                  />
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      pointerEvents: "none",
                    }}
                  >
                    <PlusOutlined
                      style={{
                        fontSize: "24px",
                        color: "#999",
                        marginBottom: "4px",
                      }}
                    />
                    <span style={{ fontSize: "12px", color: "#666" }}>
                      Agregar imagen
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <Form.Item>
            <Space>
              <Button onClick={handleCancelEdit}>Cancelar</Button>
              <Button type="primary" htmlType="submit">
                Actualizar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <NewProduct
        open={open}
        setOpen={setOpen}
        title={"Nuevo Producto"}
        vendor={vendor}
        onSubmit={onSubmit}
      />

      <CategorySelectorModal
        open={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        onAccept={handleCategorySelect}
        categoriesData={allCategories}
      />
    </div>
  );
};

export default ProductList;
